import * as momentImport from 'moment';

const moment: any = (momentImport as any).default || momentImport;

export type DateFieldStorage = 'offset' | 'iso' | 'local';

const DATE_INPUT_FORMATS = [
  'DD-MMM-YYYY',
  'D-MMM-YYYY',
  'DD MMM YYYY',
  'D MMM YYYY',
  'DD-MMMM-YYYY',
  'D-MMMM-YYYY',
  'DD MMMM YYYY',
  'D MMMM YYYY',
  'DD-MM-YYYY',
  'D-M-YYYY',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'DD/M/YYYY',
  'D/MM/YYYY',
  'YYYY-MM-DD',
];
const TIME_SUFFIXES = [' HH:mm', ' H:mm', ' HH:mm:ss', ' hh:mm A', ' h:mm A'];
const DATETIME_INPUT_FORMATS = [
  ...DATE_INPUT_FORMATS
    .filter((format) => format !== 'YYYY-MM-DD')
    .flatMap((format) => TIME_SUFFIXES.map((suffix) => format + suffix)),
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss',
];

export function dateFieldPlaceholder(dateOnly: boolean): string {
  return dateOnly ? 'DD-MMM-YYYY' : 'DD-MMM-YYYY HH:mm';
}

export function dateFieldFormatError(dateOnly: boolean): string {
  return dateOnly
    ? 'Enter a valid date such as 06-Oct-2026, 06/10/2026, or 06-10-2026.'
    : 'Enter a valid date and time such as 06-Oct-2026 14:30, 06/10/2026, or 06-10-2026 14:30.';
}

export function normalizeTypedDate(raw: string): string {
  return String(raw || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[A-Za-z]+/g, (word) => {
      if (/^(am|pm)$/i.test(word)) {
        return word.toUpperCase();
      }
      if (word.length >= 3) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word;
    });
}

/** Parse a stored or picked value into a local Date. */
export function coerceToDate(value: string | Date | null | undefined): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (ymd) {
    const year = Number(ymd[1]);
    const month = Number(ymd[2]);
    const day = Number(ymd[3]);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
      return date;
    }
    return null;
  }
  if (trimmed.includes('T') || /[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  const normalized = normalizeTypedDate(trimmed);
  const parsed = moment(normalized, [...DATE_INPUT_FORMATS, ...DATETIME_INPUT_FORMATS], true);
  return parsed.isValid() ? parsed.toDate() : null;
}

/**
 * Parse what the user typed.
 * Day comes first: 06/08/2026 and 06-08-2026 are 6 August 2026.
 * A date-time field with no time uses 00:00.
 */
export function parseTypedDate(raw: string, dateOnly: boolean): Date | null {
  const normalized = normalizeTypedDate(raw);
  if (!normalized) {
    return null;
  }
  if (!dateOnly) {
    const withTime = moment(normalized, DATETIME_INPUT_FORMATS, true);
    if (withTime.isValid()) {
      return withTime.toDate();
    }
  }
  const parsed = moment(normalized, DATE_INPUT_FORMATS, true);
  if (!parsed.isValid()) {
    return null;
  }
  return dateOnly ? parsed.toDate() : parsed.startOf('day').toDate();
}

export function formatDateField(value: string | Date | null | undefined, dateOnly: boolean): string {
  const date = coerceToDate(value);
  if (!date) {
    return '';
  }
  return moment(date).format(dateOnly ? 'DD-MMM-YYYY' : 'DD-MMM-YYYY HH:mm');
}

export function serializeDateField(date: Date, dateOnly: boolean, storage: DateFieldStorage): string {
  if (dateOnly) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
  if (storage === 'iso') {
    return date.toISOString();
  }
  if (storage === 'local') {
    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  }
  return moment(date).format('YYYY-MM-DDTHH:mm:ssZ');
}

export function dateFieldRangeError(
  date: Date,
  dateOnly: boolean,
  min: string | Date | null | undefined,
  max: string | Date | null | undefined,
): string {
  const minDate = coerceToDate(min);
  const maxDate = coerceToDate(max);
  const current = dateOnly ? startOfLocalDay(date).getTime() : date.getTime();
  if (minDate && current < (dateOnly ? startOfLocalDay(minDate).getTime() : minDate.getTime())) {
    return `Date must be on or after ${formatDateField(minDate, dateOnly)}.`;
  }
  if (maxDate && current > (dateOnly ? startOfLocalDay(maxDate).getTime() : maxDate.getTime())) {
    return `Date must be on or before ${formatDateField(maxDate, dateOnly)}.`;
  }
  return '';
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
